import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import type { paths } from "@/types/api";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

type GenerateBody =
  paths["/api/v1/documents/generate"]["post"]["requestBody"]["content"]["application/json"];
type TemplateBody =
  paths["/api/v1/documents/templates"]["post"]["requestBody"]["content"]["application/json"];

export const documentKeys = {
  forSubject: (subjectType: string, subjectId: string) =>
    ["documents", subjectType, subjectId] as const,
  templates: (type?: string) => ["documents", "templates", type ?? "all"] as const,
};

export function useDocuments(subjectType: string, subjectId: string | undefined) {
  return useQuery({
    queryKey: documentKeys.forSubject(subjectType, subjectId ?? ""),
    enabled: Boolean(subjectId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/documents", {
        params: { query: { subject_type: subjectType, subject_id: subjectId! } },
      });
      if (error || !data) throw new Error("Failed to load documents");
      return data;
    },
  });
}

export function useDocumentTemplates(type?: string) {
  return useQuery({
    queryKey: documentKeys.templates(type),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/documents/templates", {
        params: { query: type ? { type } : {} },
      });
      if (error || !data) throw new Error("Failed to load templates");
      return data;
    },
  });
}

export function useUpsertTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TemplateBody) => {
      const { data, error } = await apiClient.POST("/api/v1/documents/templates", {
        body,
      });
      if (error || !data) throw new Error("Failed to save template");
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["documents", "templates"] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await apiClient.DELETE(
        "/api/v1/documents/templates/{template_id}",
        { params: { path: { template_id: templateId } } },
      );
      if (error) throw new Error("Failed to reset template");
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["documents", "templates"] }),
  });
}

export function useGenerateDocument(subjectType: string, subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Omit<GenerateBody, "subject_type" | "subject_id">) => {
      const { data, error } = await apiClient.POST("/api/v1/documents/generate", {
        body: { ...body, subject_type: subjectType, subject_id: subjectId },
      });
      if (error || !data) throw new Error("Failed to generate document");
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: documentKeys.forSubject(subjectType, subjectId),
      }),
  });
}

export function useDeleteDocument(subjectType: string, subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string) => {
      const { error } = await apiClient.DELETE("/api/v1/documents/{document_id}", {
        params: { path: { document_id: documentId } },
      });
      if (error) throw new Error("Failed to delete document");
    },
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: documentKeys.forSubject(subjectType, subjectId),
      }),
  });
}

export function useUpdateDocumentStatus(subjectType: string, subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      documentId,
      status,
    }: {
      documentId: string;
      status: "draft" | "final";
    }) => {
      const { data, error } = await apiClient.PATCH("/api/v1/documents/{document_id}", {
        params: { path: { document_id: documentId } },
        body: { status },
      });
      if (error || !data) throw new Error("Failed to update document");
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: documentKeys.forSubject(subjectType, subjectId),
      }),
  });
}

export function useUploadDocument(subjectType: string, subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      // Multipart upload via raw fetch (openapi-fetch is JSON-oriented).
      const form = new FormData();
      form.append("file", file);
      form.append("subject_type", subjectType);
      form.append("subject_id", subjectId);
      form.append("title", file.name);
      const token = useAuthStore.getState().token;
      const res = await fetch(`${BASE_URL}/api/v1/documents/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (!res.ok) throw new Error("Upload failed");
      return res.json();
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: documentKeys.forSubject(subjectType, subjectId) }),
  });
}

export interface TemplateFieldGroup {
  group: string;
  note?: string;
  fields: { token: string; example: string }[];
}

export function useTemplateFields() {
  return useQuery({
    queryKey: ["documents", "template-fields"],
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/documents/template-fields",
      );
      if (error || !data) throw new Error("Failed to load fields");
      return data as unknown as TemplateFieldGroup[];
    },
  });
}

/** The shared document base CSS — lets the visual editor style content like the PDF. */
export function useTemplateBaseCss() {
  return useQuery({
    queryKey: ["documents", "template-base-css"],
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/documents/template-base-css",
      );
      if (error || !data) throw new Error("Failed to load base CSS");
      return (data as { css: string }).css;
    },
  });
}

export async function previewTemplate(
  body: string,
): Promise<{ html: string; error: string | null }> {
  const { data, error } = await apiClient.POST("/api/v1/documents/templates/preview", {
    body: { body },
  });
  if (error || !data) return { html: "", error: "Preview failed" };
  return { html: data.html, error: data.error ?? null };
}

export type RenderParams = {
  type: string;
  variant: string;
  language: string;
  subject_type: string;
  subject_id: string;
};

export type RenderedDocument = {
  html: string;
  title: string;
  recipient: string | null;
  error: string | null;
};

/** Renders a document to HTML with the subject's real data for A4 preview (no save). */
export async function renderDocument(params: RenderParams): Promise<RenderedDocument> {
  const { data, error } = await apiClient.POST("/api/v1/documents/render", {
    body: params,
  });
  if (error || !data) {
    return { html: "", title: "", recipient: null, error: "No template for this document" };
  }
  return {
    html: data.html,
    title: data.title,
    recipient: data.recipient ?? null,
    error: data.error ?? null,
  };
}

export async function fetchDocumentDownloadUrl(documentId: string): Promise<string> {
  const { data, error } = await apiClient.GET("/api/v1/documents/{document_id}/download", {
    params: { path: { document_id: documentId } },
  });
  if (error || !data) throw new Error("Failed to get download link");
  return data.url;
}
